import React, { useState, useEffect } from 'react';
import type { FC } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Divider, TextField, Autocomplete } from '@mui/material';
import useCustomer from '../../../hooks/useCustomer';
import { Customer, isCustomer } from '../../../types/customer';
import BasePrimaryNavItem from './BasePrimaryNavItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ShipTo } from 'src/types/orderHeaderDropdowns';
import CloseIcon from '@mui/icons-material/Close';
import CustomerSearch from 'src/components/search/CustomerSearch';
import { formatPhoneNumber } from 'src/utils/helperFunctions';
import customerApi from '../../../api/ActiveCustomerApi';
declare interface Props {
  isPopover?: boolean;
  onCloseNav?: () => void;
}

const CustomerItem: FC<React.PropsWithChildren<Props>> = (props) => {
  const { customer, saveCustomer, setPreferredShipTo } = useCustomer();
  const [showCustomerDetails, setShowCustomerDetails] = useState<Boolean>(false);
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    const init = async () => {
      if (customer && (customer.selectedShipToNumber === null || customer.selectedShipToNumber === '')) {
        let warehouse = customer.shipTos.find((w) => w.shipNo === customer.defaultShipTo).warehouseCode;
        saveCustomer({ ...customer, selectedShipToNumber: customer.defaultShipTo, selectedShipToWarehouse: warehouse });
      }
      if (customer.selectedShipToWarehouse === undefined) {
        let warehouse = customer.shipTos.find((w) => w.shipNo === (customer.selectedShipToNumber || customer.defaultShipTo))?.warehouseCode;
        saveCustomer({ ...customer, selectedShipToWarehouse: warehouse ?? customer.warehouseCode });
      }
    };

    init();
  }, [customer.numberString, customer.selectedShipToNumber, customer.defaultShipTo]);

  const menu = {
    section: 'Customer',
    items: [
      {
        title: 'Dashboard',
        path: '/',
      },
      {
        title: 'Contacts',
        path: `/${customer.number}/Contacts`,
      },
      {
        title: 'Users',
        path: `/${customer.number}/WebUsers`,
      },
      {
        title: 'Plant Volumes',
        path: `/${customer.number}/PlantVolumes`,
      },
    ],
  };

  const onChange = (value: NonNullable<string | Customer>) => {
    if (isCustomer(value) && customer.number !== value.number) {
      customerApi.getCustomer(value.numberString).then((newCustomer: Customer) => {
        saveCustomer(newCustomer);
        location.href = `/${newCustomer.number}`;
      });
    }
  };

  const onShipToChange = (newShipTo: ShipTo) => {
    setPreferredShipTo(newShipTo);
  };

  const toggleCustomerDetails = () => {
    setShowCustomerDetails(!showCustomerDetails);
  };

  const handleClose = () => {
    setOpen(!open);
  };

  const content = (
    <div onKeyDown={(e) => e.stopPropagation()}>
      <p className="global-nav__section-title">{menu.section}</p>{' '}
      <div onClick={props.onCloseNav} className="global-nav__close-icon">
        <CloseIcon />
      </div>
      <div className="global-nav__item__customersearch">
        <Box>
          <Typography color="textSecondary" variant="subtitle2">
            {` ${customer.name} (${customer.number})`}
          </Typography>
        </Box>
        <Box>
          <CustomerSearch defaultCustomer={{ ...customer }} onCustomerSelected={onChange} />
        </Box>
      </div>
      <div className="global-nav__item__shipto">
        <Box sx={{ mb: 1 }}>
          {customer.shipTos && customer.shipTos.length > 0 && (
            <Autocomplete
              multiple={false}
              id="customerSearch"
              disablePortal
              disableClearable
              options={[...customer.shipTos]}
              getOptionLabel={(option: ShipTo) => option?.description ?? ''}
              renderOption={(p, option: ShipTo) => {
                return (
                  <Box component="li" key={option.shipNo} {...p}>
                    <span style={{ fontSize: 'small' }}>{option.description}</span>
                  </Box>
                );
              }}
              onChange={(e, newValue: ShipTo) => {
                onShipToChange(newValue);
              }}
              isOptionEqualToValue={(option: ShipTo, value: ShipTo) => {
                return option.shipNo === value?.shipNo;
              }}
              value={customer.shipTos.find((x) => x.shipNo.trim() === (customer?.selectedShipToNumber ?? customer?.defaultShipTo).trim())}
              renderInput={(params) => (
                <TextField
                  focused
                  {...params}
                  InputProps={{ ...params.InputProps, style: { fontSize: 14 } }}
                  InputLabelProps={{ ...params.InputLabelProps, style: { fontSize: 14, fontWeight: 'bold' } }}
                  label="Preferred Ship To"
                />
              )}
            />
          )}
        </Box>
      </div>
      <div className={`global-nav__item global-nav__item__customer-details ${showCustomerDetails && 'is-active'} `}>
        <div className="global-nav__item-link global-nav__item__customer-details__toggle" onClick={toggleCustomerDetails}>
          Customer Details <ExpandMoreIcon />
        </div>
        {showCustomerDetails ? (
          <div className="global-nav__item__customer-details__menu">
            <p>
              <strong>Hard Goods Rep: </strong>
              {customer.hardGoodsRep}
            </p>

            <p>
              <strong>Lawn &amp; Garden Rep: </strong>
              {customer.lawnGardenRep}
            </p>

            <p>
              <strong>Plant Rep: </strong>
              {customer.plantRep}
            </p>
            <p>
              <strong>Hydro/CEA Rep: </strong>
              {customer.carrRep}
            </p>
            {customer.customerPhoneNumber && (
              <p>
                <strong>Customer Phone Number: </strong>
                {formatPhoneNumber(customer.customerPhoneNumber)}
              </p>
            )}
            <p>
              <strong>Customer Type: </strong>
              {customer.customerType ?? 'N/A'}
            </p>
            <p className="global-nav__item__customer-details__more-customer-details">
              <RouterLink to={`/${customer.number}/CustomerDetails`} onClick={() => handleClose()}>
                More Customer Details
              </RouterLink>
            </p>
          </div>
        ) : (
          <></>
        )}
      </div>
      <Divider className="d-none d-md-block" />
      <div className="global-nav__item">
        {menu.items.map((item, index) => {
          return (
            <div className="global-nav__item-link" key={index}>
              <RouterLink to={item.path} key={index} onClick={() => handleClose()}>
                {item.title}
              </RouterLink>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    // <BasePrimaryNavItem anchorEl={customer.name ? menu.section + customer.name : menu.section} isPopover={props.isPopover}>
    <BasePrimaryNavItem
      id="customerMenuButton"
      title={`Customer #: ${customer.number}`}
      anchorEl={customer.name ? <div className="global-header__customer--active">{customer.name}</div> : menu.section}
      isPopover={props.isPopover}
      isOpen={open}
    >
      <div className="global-nav__customer">{content}</div>
    </BasePrimaryNavItem>
  );
};

export default CustomerItem;
